/**
 * "Is the school serving this day at all?" for the family week grid.
 *
 * A day the school doesn't serve — a blocked date, a weekday switched off on
 * `MenuServiceLine.serviceDays`, or a day outside the school session — must
 * read as NO SERVICE, not as an orderable day whose menu happens to be empty.
 * Before this, Candeo's non-service Wednesdays surfaced with an "Order by …"
 * countdown and a "Menu still in the works" placeholder (Mark Valencia, Ops,
 * 2026-08-04), which is indistinguishable from a menu nobody had finished
 * authoring.
 *
 * Pure so the roll-up rules are unit-tested without rendering.
 */

import type { MenuWeekPreviewDay } from '@/api/orders'
import type { FamilyOrderLocation } from '@/components/orders/family/week/columns'
import { childLocationRow } from '@/components/orders/family/week/columns'
import type { DayOrderStatus } from '@/lib/family-orders/day-status'

/**
 * Band scoping for a campus's menu teaser (2026-07 fail-closed grade-band
 * gate): when every child at the campus shares one band, scope the teaser to
 * it; mixed bands fetch unfiltered (`undefined` → no band param) because the
 * day view filters per child and `placeOrder` re-enforces the rule at submit.
 *
 * `null` means every child there is band-less — they can see no menus at all,
 * so the caller skips the fetch entirely.
 */
export function sharedBandForLocation(location: FamilyOrderLocation): string | null | undefined {
  const bands = new Set(location.children.map((child) => childLocationRow(child, location.id)?.gradeRangeCode ?? null))
  return bands.size === 1 ? (bands.values().next().value ?? null) : undefined
}

/**
 * Roll per-campus day previews up into one verdict for the day card.
 *
 * True only when EVERY campus has resolved data saying it isn't serving. An
 * unresolved campus (pending fetch, band-less student, pre-flag cached payload
 * with no `noService` field) counts as unknown — never as closed — so a slow
 * or stale response can't grey out a day a family could still order on. A
 * family with no campuses is likewise never "no service".
 */
export function dayIsNoService(days: ReadonlyArray<MenuWeekPreviewDay | null | undefined>): boolean {
  return days.length > 0 && days.every((day) => day?.noService === true)
}

/** The badge statuses that mean an order exists for the day. */
const PLACED_STATUSES: ReadonlySet<DayOrderStatus> = new Set<DayOrderStatus>(['COMPLETE', 'EDIT_BY', 'PAYMENT_PENDING'])

/**
 * How the day card presents a no-service day.
 *
 * A day switched off AFTER a family ordered on it still has an order to show,
 * so it keeps its real badge and stays navigable — greying it out would strand
 * the parent's receipt. Only an unordered no-service day reads "No service" and
 * goes inert (alongside the pre-existing inert case: closed with nothing
 * ordered, which has no day view worth visiting).
 */
export function noServiceCardState(args: {
  noService: boolean
  status: DayOrderStatus
  /**
   * The card holds a view-only campus section (school ordering only,
   * PROD-1554). Its menu is worth opening on every served day, past or
   * future, so "closed with nothing ordered" must not make the card inert.
   */
  hasViewOnlyCampus?: boolean
}): {
  showNoService: boolean
  disabled: boolean
} {
  const hasOrder = PLACED_STATUSES.has(args.status)
  const showNoService = args.noService && !hasOrder
  const closedWithNothingOrdered = args.status === 'NOT_ORDERED' && !args.hasViewOnlyCampus
  return { showNoService, disabled: showNoService || closedWithNothingOrdered }
}

/**
 * Whether ONE campus's section of a day card reads as closed — menu teaser
 * hidden, "No meals ordered" copy. Ordering campuses follow the card's merged
 * pill, exactly as before. A view-only campus (school ordering only) closes
 * only when the school isn't serving: there, the menu IS the content, on past
 * and future days alike.
 */
export function campusSectionClosed(args: {
  viewOnly: boolean
  showNoService: boolean
  status: DayOrderStatus
}): boolean {
  if (args.viewOnly) {
    return args.showNoService
  }
  return args.showNoService || args.status === 'NOT_ORDERED' || args.status === 'COMPLETE'
}
